import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Card,
  Form,
  Table,
  Button,
  Modal,
  Dropdown,
  Col,
  Accordion,
  Image,
  Nav,
} from "react-bootstrap";
import { FaSearch, FaEdit, FaTrash, FaKey, FaFilter } from "react-icons/fa";
import "../css/UserManagement.css";
import { jwtDecode } from "jwt-decode";
import { userService } from "../services/userService";
import { accountService } from "../services/accountService";
import userManagementIcon from "../assets/images/icons/user-management-icon.png";
import searchIcon from "../assets/images/icons/search.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { IoFilterOutline } from "react-icons/io5";
import Tab from "react-bootstrap/Tab";

import { FaEllipsisH } from "react-icons/fa";
import { RiDeleteBin5Line } from "react-icons/ri";
import { GrEdit } from "react-icons/gr";
import { FaEllipsisV } from "react-icons/fa";
import noRecordFound from "../assets/images/no-record.png";
import { FcClearFilters } from "react-icons/fc";
import customDomainIcon from "../assets/images/icons/custom-domain-icon.png";
import companyImage from "../assets/images/icons/CompanyIcon.png";
import { TfiAngleDown } from "react-icons/tfi";
import { TfiAngleUp } from "react-icons/tfi";
import usersIcon from "../assets/images/icons/usersIcon.png";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import clearFilterIcon from "../assets/images/icons/Filters-lines.png";
import CompanySidebar from "../components/CompanySidebar";
import { authService } from "../services/authService";

const UserManagement = ({
  embedded = false,
  selectedCompanyId: selectedCompanyIdProp = null,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    contactNo: "",
    email: "",
    role: "",
    status: "",
  });
  const recordsPerPage = 10;
  const [totalPages, setTotalPages] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    email: "",
    contactNo: "",
    accountId: "",
    roles: ["USER"],
  });
  const availableRoles = ["USER", "ADMIN"];
  const availableStatuses = ["Active", "Inactive"];
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("ALL");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    selectedCompanyIdProp
  );
  const [companyUsers, setCompanyUsers] = useState([]);
  const [userAccountId, setUserAccountId] = useState(null);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const usersPerPage = 4;
  const [superAdminSearchTerm, setSuperAdminSearchTerm] = useState("");
  const [superAdminFilterType, setSuperAdminFilterType] = useState("ALL");
  const [companyUserCounts, setCompanyUserCounts] = useState({});
  const [companyLogos, setCompanyLogos] = useState({});
  const [validationErrors, setValidationErrors] = useState({
    username: "",
    password: "",
    email: "",
    contactNo: "",
    accountId: "",
    roles: "",
  });
  const [editValidationErrors, setEditValidationErrors] = useState({
    email: "",
    contactNo: "",
    role: "",
    status: "",
  });
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);

  const getUserStatus = (user) => {
    return user && user.enabled ? "Active" : "Inactive";
  };

  // Server-side pagination and filtering: render current page records from API
  const currentRecords = users;

  const fetchCompanyLogo = async (fileName) => {
    if (!fileName) {
      return null;
    }

    try {
      const blob = await accountService.downloadLogo(fileName);

      if (blob && blob.size > 0) {
        // Ensure we have a proper MIME type for the blob
        let mimeType = blob.type;
        if (!mimeType || mimeType === "application/octet-stream") {
          mimeType = "image/png";
        }

        // Convert blob to base64 data URL
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            let dataUrl = reader.result;

            if (dataUrl && !dataUrl.startsWith("data:image/")) {
              const base64Data = dataUrl.split(",")[1] || dataUrl;
              dataUrl = `data:${mimeType};base64,${base64Data}`;
            }

            resolve(dataUrl);
          };
          reader.onerror = () => {
            console.error("Error reading blob as data URL in UserManagement");
            reject(reader.error);
          };
          reader.readAsDataURL(blob);
        });
      }
      return null;
    } catch (error) {
      console.error("Error fetching company logo in UserManagement:", error);
      return null;
    }
  };

  const fetchCompanies = async (shouldFetchLogos = true) => {
    try {
      const response = await accountService.getAccountConfigs();

      if (response && response.length > 0) {
        const companiesData = Array.isArray(response) ? response : [response];
        setCompanies(companiesData);

        // Only fetch logos if explicitly requested (for super admin view)
        if (shouldFetchLogos) {
          const logoPromises = companiesData.map(async (company) => {
            if (company.logo) {
              const logoUrl = await fetchCompanyLogo(company.logo);
              return [company.id, logoUrl];
            }
            return [company.id, null];
          });

          const logos = await Promise.all(logoPromises);
          const logoMap = Object.fromEntries(logos);
          setCompanyLogos(logoMap);
        }

        const firstCompanyId = companiesData[0].id;
        setSelectedCompanyId(firstCompanyId);
      }
    } catch (err) {
      console.error("Failed to fetch companies:", err);
    }
  };

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const decodedToken = jwtDecode(token);
          const isSuperAdminUser = decodedToken.roles.includes("SUPERADMIN");
          setIsSuperAdmin(isSuperAdminUser);
          const isAdmin = decodedToken.roles.includes("ADMIN");

          // Fetch companies with logos only for super admin, without logos for regular admin
          await fetchCompanies(isSuperAdminUser);

          if (isAdmin && !isSuperAdminUser) {
            const userResponse = await userService.getUserByUsername(
              decodedToken.sub
            );
            if (userResponse) {
              const accountId = userResponse.accountId;
              setUserAccountId(accountId);
              fetchUsers(accountId, 1);
            }
          }
        }
      } catch (err) {
        console.error("Error decoding token:", err);
        setIsSuperAdmin(false);
      }
    };
    checkUserRole();
  }, []);

  useEffect(() => {
    if (selectedCompanyIdProp) {
      setSelectedCompanyId(selectedCompanyIdProp);
    }
  }, [selectedCompanyIdProp]);

  useEffect(() => {
    if (isSuperAdmin && selectedCompanyId) {
      fetchUsers(selectedCompanyId, 1);
    }
  }, [selectedCompanyId, isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin && companies.length > 0 && !selectedCompanyId) {
      fetchUsers(companies[0].id, 1);
    }
  }, [companies, isSuperAdmin, selectedCompanyId]);

  // Refetch when search or filter changes to use server-side filtering
  useEffect(() => {
    const accountId = userAccountId;
    if (accountId) {
      fetchUsers(accountId, 1);
    }
  }, [searchTerm, filterType]);

  const fetchUsers = async (accountId, page = 1) => {
    console.log("Fetching users for accountId:", accountId, "page:", page);
    try {
      if (accountId) {
        const effectiveSearch = isSuperAdmin
          ? superAdminSearchTerm
          : searchTerm;
        const effectiveFilterType = isSuperAdmin
          ? superAdminFilterType
          : filterType;
        const statusFilter =
          effectiveFilterType === "ACTIVE"
            ? "ACTIVE"
            : effectiveFilterType === "INACTIVE"
            ? "INACTIVE"
            : undefined;
        const roleFilter =
          effectiveFilterType === "ADMIN"
            ? "ADMIN"
            : effectiveFilterType === "USER"
            ? "USER"
            : undefined;

        const response = await userService.getUsersByAccount(accountId, {
          page: page - 1,
          size: recordsPerPage,
          search: effectiveSearch || undefined,
          status: statusFilter,
          role: roleFilter,
        });

        if (response) {
          setUsers(response.content || []);
          setCompanyUsers(response.content || []);
          setTotalPages(response.totalPages || 0);
          setCurrentPage((response.page || 0) + 1);
          setCurrentUserPage((response.page || 0) + 1);
        }
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
      // If backend returns 404 for no users, clear current lists
      if (err.response && err.response.status === 404) {
        setUsers([]);
        setCompanyUsers([]);
        setTotalPages(0);
      }
    } finally {
      setLoading(false);
      // Mark that data fetch completed so empty state renders properly in superadmin view
      setDataFetched(true);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();

    const errors = {};
    Object.keys(newUser).forEach((field) => {
      const error = validateField(
        field,
        field === "roles" ? newUser[field][0] : newUser[field]
      );
      if (error) errors[field] = error;
    });

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please fix all validation errors before submitting");
      return;
    }

    try {
      setIsLoading(true);
      const response = await userService.createUser(newUser);
      // console.log("Add user response:", response);
      if (response) {
        setUsers((prevUsers) => [...prevUsers, response]);
        if (selectedCompanyId === newUser.accountId) {
          setCompanyUsers((prevUsers) => [...prevUsers, response]);
        }
        setShowAddUserModal(false);
        setNewUser({
          username: "",
          password: "",
          email: "",
          contactNo: "",
          accountId: "",
          roles: ["USER"],
        });
        toast.success("User added successfully!");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to add user";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const accountId = isSuperAdmin
      ? selectedCompanyId || userAccountId
      : userAccountId;
    if (accountId) {
      fetchUsers(accountId, pageNumber);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleEditClick = (user) => {
    setUserToEdit(user);
    setEditFormData({
      contactNo: user.contactNo,
      email: user.email,
      role: user.roles ? user.roles[0] : "",
      status: user.enabled ? "Active" : "Inactive",
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value,
    });

    const error = validateField(name, value);
    setEditValidationErrors((prev) => ({ ...prev, [name]: error }));
  };

  const confirmDelete = async () => {
    try {
      await userService.deleteUser(userToDelete.id);

      setUsers((prevUsers) =>
        prevUsers.filter((user) => user.id !== userToDelete.id)
      );

      if (selectedCompanyId) {
        setCompanyUsers((prevUsers) =>
          prevUsers.filter((user) => user.id !== userToDelete.id)
        );
      }

      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (err) {
      console.error(
        "Failed to delete user:",
        err.response?.data?.message || "Delete failed"
      );
    }
  };

  const saveEditChanges = async () => {
    const errors = {};
    Object.keys(editFormData).forEach((field) => {
      const error = validateField(field, editFormData[field]);
      if (error) errors[field] = error;
    });

    setEditValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please fix all validation errors before submitting");
      return;
    }

    try {
      setIsLoading(true);
      await userService.updateRegisteredUser(userToEdit.id, {
        email: editFormData.email,
        contactNo: editFormData.contactNo,
        roles: [editFormData.role],
        enabled: editFormData.status === "Active",
      });

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userToEdit.id
            ? {
                ...user,
                email: editFormData.email,
                contactNo: editFormData.contactNo,
                roles: [editFormData.role],
                enabled: editFormData.status === "Active",
              }
            : user
        )
      );

      if (selectedCompanyId) {
        setCompanyUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userToEdit.id
              ? {
                  ...user,
                  email: editFormData.email,
                  contactNo: editFormData.contactNo,
                  roles: [editFormData.role],
                  enabled: editFormData.status === "Active",
                }
              : user
          )
        );
      }

      setShowEditModal(false);
      setUserToEdit(null);
      toast.success("User updated successfully!");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to update user";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = (e) => {
    setNewUser({ ...newUser, roles: [e.target.value] });
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const handleFilterChange = (filterType) => {
    setFilterType(filterType);
    setCurrentPage(1);
  };

  const [isLoading, setIsLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);

  const fetchCompanyUsers = async (companyId) => {
    try {
      setIsLoading(true);
      setDataFetched(false);

      const response = await userService.getUsersByAccount(companyId);

      if (response) {
        setCompanyUsers(response);
        setUsers(response);

        // Update user counts to avoid separate API call
        setCompanyUserCounts((prev) => ({
          ...prev,
          [companyId]: response.length,
        }));
      }
      setSelectedCompanyId(companyId);
      setDataFetched(true);
    } catch (err) {
      console.error("Failed to fetch company users:", err);
      setCompanyUsers([]);
      setCompanyUserCounts((prev) => ({
        ...prev,
        [companyId]: 0,
      }));
      setDataFetched(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setCurrentUserPage(1);
  }, [selectedCompanyId]);

  // Refetch company users when super admin filter/search changes
  useEffect(() => {
    if (isSuperAdmin && selectedCompanyId) {
      fetchUsers(selectedCompanyId, 1);
    }
  }, [superAdminSearchTerm, superAdminFilterType]);

  const getFilteredCompanyUsers = () => {
    // Server returns already-filtered, paginated company users
    return companyUsers || [];
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateContactNo = (contactNo) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(contactNo);
  };

  const validatePassword = (password) => {
    return password.length >= 8;
  };

  const validateAddUserForm = () => {
    const errors = {};

    if (!newUser.username.trim()) {
      errors.username = "Username is required";
    } else if (newUser.username.length < 3) {
      errors.username = "Username must be at least 3 characters";
    }

    if (!newUser.password) {
      errors.password = "Password is required";
    } else if (!validatePassword(newUser.password)) {
      errors.password = "Password must be at least 8 characters";
    }

    if (!newUser.email) {
      errors.email = "Email is required";
    } else if (!validateEmail(newUser.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!newUser.contactNo) {
      errors.contactNo = "Contact number is required";
    } else if (!validateContactNo(newUser.contactNo)) {
      errors.contactNo = "Please enter a valid 10-digit contact number";
    }

    if (!newUser.accountId) {
      errors.accountId = "Company selection is required";
    }

    if (!newUser.roles.length) {
      errors.roles = "Role selection is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateEditForm = () => {
    const errors = {};

    if (!editFormData.email) {
      errors.email = "Email is required";
    } else if (!validateEmail(editFormData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!editFormData.contactNo) {
      errors.contactNo = "Contact number is required";
    } else if (!validateContactNo(editFormData.contactNo)) {
      errors.contactNo = "Please enter a valid 10-digit contact number";
    }

    if (!editFormData.role) {
      errors.role = "Role selection is required";
    }

    if (!editFormData.status) {
      errors.status = "Status selection is required";
    }

    setEditValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateField = (name, value) => {
    switch (name) {
      case "username":
        if (!value.trim()) return "Username is required";
        if (value.length < 3) return "Username must be at least 3 characters";
        if (value.length > 50)
          return "Username must be less than 50 characters";
        if (!/^[a-zA-Z0-9_]+$/.test(value))
          return "Username can only contain letters, numbers, and underscores";
        return "";

      case "password":
        if (!value) return "Password is required";
        if (value.length < 8) return "Password must be at least 8 characters";
        if (!/(?=.*[a-z])/.test(value))
          return "Password must contain at least one lowercase letter";
        if (!/(?=.*[A-Z])/.test(value))
          return "Password must contain at least one uppercase letter";
        if (!/(?=.*\d)/.test(value))
          return "Password must contain at least one number";
        if (!/(?=.*[@$!%*?&])/.test(value))
          return "Password must contain at least one special character";
        return "";

      case "email":
        if (!value) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Please enter a valid email address";
        return "";

      case "contactNo":
        if (!value) return "Contact number is required";
        if (!/^\d{10}$/.test(value)) return "Contact number must be 10 digits";
        return "";

      case "accountId":
        if (!value) return "Company selection is required";
        return "";

      case "roles":
        if (!value || value.length === 0) return "Role selection is required";
        return "";

      default:
        return "";
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "roles") {
      setNewUser((prev) => ({ ...prev, [name]: [value] }));
    } else {
      setNewUser((prev) => ({ ...prev, [name]: value }));
    }

    const error = validateField(name, name === "roles" ? [value] : value);
    setValidationErrors((prev) => ({ ...prev, [name]: error }));
  };

  return (
    <Container fluid className="ps-0 pb-4 pe-0 overflow-hidden bg-white">
      {isSuperAdmin ? (
        <Card className="card-nostyle tab-card">
          <Card.Body className="p-0">
            {/* <div
              className="d-flex justify-content-between ps-4 pb-3 pt-3 pe-4 align-items-center w-100"
              style={{ borderBottom: "1px solid #EAECF0" }}
            >
              <h4 className="mb-0 bg-white br-radius-12 rounded-bottom-0 fs-20 mb-0 poppins fw-600">
                <Image src={customDomainIcon} className="me-1" width="20" />{" "}
                User Management Dashboard
              </h4>
            </div> */}

            <Card className="card-nostyle border-0 shadow-none">
              <Card.Body className="ps-0 pe-0 pt-0">
                <Tab.Container
                  // activeKey={selectedCompanyId}
                  // onSelect={(k) => fetchCompanyUsers(k)}
                  activeKey={selectedCompanyId}
                  onSelect={(k) => {
                    setSelectedCompanyId(k);
                    fetchCompanyUsers(k);
                  }}
                >
                  <Row className="">
                    {/* Left Vertical Tabs */}
                    {!embedded && (
                      <Col lg={4}>
                        <CompanySidebar
                          companies={companies}
                          selectedCompanyId={selectedCompanyId}
                          onSelectCompany={(id) => {
                            setSelectedCompanyId(id);
                            fetchCompanyUsers(id);
                          }}
                          searchTerm={companySearchTerm}
                          onSearchTermChange={setCompanySearchTerm}
                          companyLogos={companyLogos}
                        />
                      </Col>
                    )}

                    {/* Right Content Area */}
                    <Col lg={embedded ? 12 : 8}>
                      <Tab.Content className="cus-tab-content">
                        {companies.map((company) => (
                          <Tab.Pane key={company.id} eventKey={company.id}>
                            {selectedCompanyId === company.id && (
                              <>
                                <h6
                                  className="company-dark-bg-logo"
                                  style={{ fontSize: "13px" }}
                                >
                                  {company.accountName}
                                </h6>

                                <div
                                  className="company-detail w-100 d-flex align-items-center gap-2 justify-content-between mb-2"
                                  style={{
                                    borderBottom:
                                      "1px solid rgb(228, 228, 228)",
                                    padding: "8px 8px 10px 8px",
                                  }}
                                >
                                  <div className="d-flex flex-column">
                                    <div className="d-flex align-items-center gap-2">
                                      <Image
                                        width={40}
                                        height={40}
                                        src={
                                          companyLogos[company.id] ||
                                          companyImage
                                        }
                                        style={{
                                          borderRadius: "50%",
                                          border: "1px solid #E4E7EC",
                                          objectFit: "contain",
                                        }}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = companyImage;
                                        }}
                                      />
                                      <div>
                                        <h6
                                          className="text-101828 ls-0 mb-0 text-capitalize fw-600"
                                          style={{ fontSize: "13px" }}
                                        >
                                          {company.accountName}
                                        </h6>
                                        <p className="mb-0">
                                          <span className="text-muted fs-12">
                                            <Image
                                              width={12}
                                              className="me-2"
                                              src={usersIcon}
                                            />
                                            {companyUsers.length} Users
                                          </span>
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    {company.isActive ? (
                                      <span className="badge-custom badge-border-success badge badge-before position-relative ps-3 bg-success">
                                        Active
                                      </span>
                                    ) : (
                                      <span className="badge-custom badge-border-danger badge badge-before position-relative ps-3 bg-danger">
                                        Inactive
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div
                                  style={{ padding: "10px" }}
                                  className="company-table"
                                >
                                  {/* ================= Toolbar (ALWAYS VISIBLE) ================= */}
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center gap-3">
                                      {/* Search */}
                                      <div className="position-relative">
                                        <input
                                          type="text"
                                          placeholder="Search users..."
                                          className="form-control"
                                          value={superAdminSearchTerm}
                                          onChange={(e) =>
                                            setSuperAdminSearchTerm(
                                              e.target.value
                                            )
                                          }
                                          style={{
                                            width: "250px",
                                            paddingRight: "40px",
                                          }}
                                        />
                                        <Image
                                          style={{ right: "15px", top: "12px" }}
                                          width={15}
                                          className="position-absolute"
                                          src={searchIcon}
                                        />
                                      </div>

                                      {/* Filter */}
                                      <Dropdown>
                                        <Dropdown.Toggle
                                          variant="light"
                                          className="bg-white"
                                          style={{
                                            borderRadius: "8px",
                                            borderColor: "#C2C2C2",
                                          }}
                                        >
                                          <IoFilterOutline className="me-2" />
                                          {superAdminFilterType === "ALL"
                                            ? "All Users"
                                            : superAdminFilterType === "ACTIVE"
                                            ? "Active Users"
                                            : superAdminFilterType ===
                                              "INACTIVE"
                                            ? "Inactive Users"
                                            : superAdminFilterType === "ADMIN"
                                            ? "Admin Users"
                                            : superAdminFilterType === "USER"
                                            ? "Regular Users"
                                            : "Filter"}
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                          <Dropdown.Item
                                            active={
                                              superAdminFilterType === "ALL"
                                            }
                                            onClick={() =>
                                              setSuperAdminFilterType("ALL")
                                            }
                                          >
                                            All Users
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            active={
                                              superAdminFilterType === "ACTIVE"
                                            }
                                            onClick={() =>
                                              setSuperAdminFilterType("ACTIVE")
                                            }
                                          >
                                            Active Users
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            active={
                                              superAdminFilterType ===
                                              "INACTIVE"
                                            }
                                            onClick={() =>
                                              setSuperAdminFilterType(
                                                "INACTIVE"
                                              )
                                            }
                                          >
                                            Inactive Users
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            active={
                                              superAdminFilterType === "ADMIN"
                                            }
                                            onClick={() =>
                                              setSuperAdminFilterType("ADMIN")
                                            }
                                          >
                                            Admin Users
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            active={
                                              superAdminFilterType === "USER"
                                            }
                                            onClick={() =>
                                              setSuperAdminFilterType("USER")
                                            }
                                          >
                                            Regular Users
                                          </Dropdown.Item>
                                        </Dropdown.Menu>
                                      </Dropdown>

                                      {/* Clear Filter */}
                                      {superAdminFilterType !== "ALL" && (
                                        <Button
                                          variant="light"
                                          style={{ padding: "10px" }}
                                          onClick={() =>
                                            setSuperAdminFilterType("ALL")
                                          }
                                          title="Clear Filter"
                                        >
                                          <Image
                                            src={clearFilterIcon}
                                            width={16}
                                          />
                                        </Button>
                                      )}
                                    </div>
                                  </div>

                                  {/* ================= Content ================= */}
                                  {isLoading ? (
                                    <p className="text-center fs-14 fw-500">
                                      Loading...
                                    </p>
                                  ) : getFilteredCompanyUsers().length === 0 ? (
                                    <div className="text-center pb-2">
                                      <Image width={100} src={noRecordFound} />
                                      <p className="fw-600 text-101828 mb-1 mt-2">
                                        {superAdminSearchTerm ||
                                        superAdminFilterType !== "ALL"
                                          ? `No records found ${
                                              superAdminFilterType !== "ALL"
                                                ? `for ${superAdminFilterType.toLowerCase()}`
                                                : ""
                                            } ${
                                              superAdminSearchTerm
                                                ? `"${superAdminSearchTerm}"`
                                                : ""
                                            }`
                                          : "No Previous Records !"}
                                      </p>
                                    </div>
                                  ) : (
                                    <>
                                      {/* ================= Table ================= */}
                                      <Table
                                        responsive
                                        hover
                                        className="mb-0 table-res"
                                        style={{ tableLayout: "inherit" }}
                                      >
                                        <thead>
                                          <tr>
                                            <th>Sr No.</th>
                                            <th>Username</th>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                          </tr>
                                        </thead>

                                        <tbody>
                                          {getFilteredCompanyUsers().map(
                                            (user, index) => {
                                              const status =
                                                getUserStatus(user);
                                              return (
                                                <tr key={user.id}>
                                                  <td>
                                                    {(currentPage - 1) *
                                                      recordsPerPage +
                                                      index +
                                                      1}
                                                  </td>
                                                  <td>{user.username}</td>

                                                  <td style={{ width: "20%" }}>
                                                    <p
                                                      className="mb-0"
                                                      style={{
                                                        width: "100px",
                                                        overflow: "hidden",
                                                        textOverflow:
                                                          "ellipsis",
                                                        whiteSpace: "nowrap",
                                                      }}
                                                      title={user.email}
                                                    >
                                                      {user.email}
                                                    </p>
                                                  </td>

                                                  <td>
                                                    {user.roles?.join(", ")}
                                                  </td>

                                                  <td>
                                                    <span
                                                      className={`badge bg-${
                                                        status === "Active"
                                                          ? "success"
                                                          : "danger"
                                                      }`}
                                                    >
                                                      {status}
                                                    </span>
                                                  </td>

                                                  <td>
                                                    <Dropdown className="cust-drop">
                                                      <Dropdown.Toggle variant="link">
                                                        <FaEllipsisV className="text-A8B0B9 fs-12" />
                                                      </Dropdown.Toggle>
                                                      <Dropdown.Menu>
                                                        <Dropdown.Item
                                                          onClick={() =>
                                                            handleEditClick(
                                                              user
                                                            )
                                                          }
                                                          className="fs-12 pt-2 pb-2"
                                                        >
                                                          <GrEdit className="me-1" />{" "}
                                                          Edit
                                                        </Dropdown.Item>
                                                        <Dropdown.Item
                                                          onClick={() =>
                                                            handleDeleteClick(
                                                              user
                                                            )
                                                          }
                                                          className="fs-12 pt-2 pb-2"
                                                        >
                                                          <RiDeleteBin5Line className="me-1" />{" "}
                                                          Delete
                                                        </Dropdown.Item>
                                                      </Dropdown.Menu>
                                                    </Dropdown>
                                                  </td>
                                                </tr>
                                              );
                                            }
                                          )}
                                        </tbody>
                                      </Table>

                                      {/* ================= Pagination ================= */}
                                      {totalPages > 1 && (
                                        <div className="d-flex justify-content-center mt-3">
                                          <ul className="pagination mb-2">
                                            <li
                                              className={`page-item ${
                                                currentPage === 1
                                                  ? "disabled"
                                                  : ""
                                              }`}
                                            >
                                              <button
                                                className="page-link"
                                                onClick={() =>
                                                  handlePageChange(1)
                                                }
                                                disabled={currentPage === 1}
                                              >
                                                &laquo;
                                              </button>
                                            </li>

                                            <li
                                              className={`page-item ${
                                                currentPage === 1
                                                  ? "disabled"
                                                  : ""
                                              }`}
                                            >
                                              <button
                                                className="page-link"
                                                onClick={() =>
                                                  handlePageChange(
                                                    currentPage - 1
                                                  )
                                                }
                                                disabled={currentPage === 1}
                                              >
                                                &lt;
                                              </button>
                                            </li>

                                            {[
                                              ...Array(Math.min(3, totalPages)),
                                            ].map((_, idx) => {
                                              let page = Math.max(
                                                1,
                                                Math.min(
                                                  currentPage - 1 + idx,
                                                  totalPages
                                                )
                                              );
                                              return (
                                                <li
                                                  key={page}
                                                  className={`page-item ${
                                                    currentPage === page
                                                      ? "active"
                                                      : ""
                                                  }`}
                                                >
                                                  <button
                                                    className="page-link"
                                                    onClick={() =>
                                                      handlePageChange(page)
                                                    }
                                                  >
                                                    {page}
                                                  </button>
                                                </li>
                                              );
                                            })}

                                            <li
                                              className={`page-item ${
                                                currentPage === totalPages
                                                  ? "disabled"
                                                  : ""
                                              }`}
                                            >
                                              <button
                                                className="page-link"
                                                onClick={() =>
                                                  handlePageChange(
                                                    currentPage + 1
                                                  )
                                                }
                                                disabled={
                                                  currentPage === totalPages
                                                }
                                              >
                                                &gt;
                                              </button>
                                            </li>

                                            <li
                                              className={`page-item ${
                                                currentPage === totalPages
                                                  ? "disabled"
                                                  : ""
                                              }`}
                                            >
                                              <button
                                                className="page-link"
                                                onClick={() =>
                                                  handlePageChange(totalPages)
                                                }
                                                disabled={
                                                  currentPage === totalPages
                                                }
                                              >
                                                &raquo;
                                              </button>
                                            </li>
                                          </ul>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </>
                            )}
                          </Tab.Pane>
                        ))}
                      </Tab.Content>
                    </Col>
                  </Row>
                </Tab.Container>
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      ) : (
        <Card
          className="card-nostyle border-0 shadow-none"
          style={{ marginTop: "70px" }}
        >
          <Card.Body className="ps-0 pe-0">
            <div
              className="d-flex justify-content-between ps-4 pe-4 align-items-center"
              style={{ borderBottom: "1px solid #EAECF0" }}
            >
              <h4 className="mb-0 bg-white br-radius-12 rounded-bottom-0 pb-3 fs-20 poppins fw-600">
                <Image src={userManagementIcon} className="me-1" width="20" />{" "}
                User Management Dashboard
              </h4>
            </div>

            <div
              className="d-flex justify-content-between align-items-center mb-4 pb-3 pt-3 ps-4 pe-4"
              style={{ borderBottom: "1px solid #EAECF0" }}
            >
              <div className="d-flex gap-3 w-100 justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div className="position-relative">
                    <input
                      type="text"
                      placeholder="Search users..."
                      className="form-control"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ width: "250px", paddingRight: "40px" }}
                    />
                    <Image
                      style={{ right: "15px", top: "12px" }}
                      width={15}
                      className="position-absolute"
                      src={searchIcon}
                    />
                  </div>

                  <Dropdown>
                    <Dropdown.Toggle
                      variant="light"
                      className="bg-white cus-dropdown-toggle-show"
                      style={{ borderRadius: "8px", borderColor: "#C2C2C2" }}
                    >
                      <IoFilterOutline className="me-2" />
                      {filterType === "ALL"
                        ? "All Users"
                        : filterType === "ACTIVE"
                        ? "Active Users"
                        : filterType === "INACTIVE"
                        ? "Inactive Users"
                        : filterType === "ADMIN"
                        ? "Admin Users"
                        : filterType === "USER"
                        ? "Regular Users"
                        : "Filter"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item
                        active={filterType === "ALL"}
                        onClick={() => handleFilterChange("ALL")}
                      >
                        All Users
                      </Dropdown.Item>
                      <Dropdown.Item
                        active={filterType === "ACTIVE"}
                        onClick={() => handleFilterChange("ACTIVE")}
                      >
                        Active Users
                      </Dropdown.Item>
                      <Dropdown.Item
                        active={filterType === "INACTIVE"}
                        onClick={() => handleFilterChange("INACTIVE")}
                      >
                        Inactive Users
                      </Dropdown.Item>
                      <Dropdown.Item
                        active={filterType === "ADMIN"}
                        onClick={() => handleFilterChange("ADMIN")}
                      >
                        Admin Users
                      </Dropdown.Item>
                      <Dropdown.Item
                        active={filterType === "USER"}
                        onClick={() => handleFilterChange("USER")}
                      >
                        Regular Users
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  {filterType !== "ALL" && (
                    <Button
                      variant="light"
                      className=""
                      style={{ padding: "10px 10px" }}
                      onClick={() => handleFilterChange("ALL")}
                      title="Clear Filter"
                    >
                      <FcClearFilters icon={faPlus} className="fs-16" />
                    </Button>
                  )}
                </div>

                <Button
                  variant="success"
                  className=""
                  style={{ borderRadius: "8px" }}
                  onClick={() => {
                    setShowAddUserModal(true);
                    if (userAccountId) {
                      setNewUser((prev) => ({
                        ...prev,
                        accountId: userAccountId,
                      }));
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} className="fs-16 me-1" /> Add
                  User
                </Button>
              </div>
            </div>

            {/* {filterType !== 'ALL' && (
              <div className="mb-3">
                <span className="text-muted">
                  Filtered by: {' '}
                  <span className="badge bg-info">
                    {filterType === 'ACTIVE' ? 'Active Users' :
                     filterType === 'INACTIVE' ? 'Inactive Users' :
                     filterType === 'ADMIN' ? 'Admin Users' :
                     'Regular Users'}
                  </span>
                  <Button
                    variant="link"
                    className="text-decoration-none p-0 ms-2"
                    onClick={() => handleFilterChange('ALL')}
                  >
                    Clear filter
                  </Button>
                </span>
              </div>
            )} */}

            <div className="ps-4 pe-4">
              <Table
                responsive
                hover
                className="mb-0 table-hover-border table-overflow-unset overflow-unset"
              >
                <thead>
                  <tr>
                    <th>Sr No.</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <Image width={100} src={noRecordFound} />
                        <p className="fw-600 text-101828 mb-1 mt-2">
                          {searchTerm || filterType !== "ALL"
                            ? `No records found ${
                                filterType !== "ALL"
                                  ? `for ${filterType.toLowerCase()}`
                                  : ""
                              } ${searchTerm ? `"${searchTerm}"` : ""}`
                            : "No Previous Records !"}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    currentRecords.map((user, index) => {
                      const status = getUserStatus(user);
                      return (
                        <tr key={user.id}>
                          <td>
                            {(currentPage - 1) * recordsPerPage + index + 1}
                          </td>
                          <td className="fw-500">{user.username}</td>
                          <td>{user.email}</td>
                          <td>{user.roles?.join(", ")}</td>
                          <td>
                            <span
                              className={`badge badge-before position-relative ps-3 bg-${
                                status === "Active" ? "success" : "danger"
                              }`}
                            >
                              {status}
                            </span>
                          </td>
                          <td>
                            {/* <div className="d-flex gap-2">
                            <Button 
                              variant="outline-success" 
                              size="sm"
                              onClick={() => handleEditClick(user)}
                            >
                              <FaEdit />
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleDeleteClick(user)}
                            >
                              <FaTrash />
                            </Button>
                          </div> */}

                            <Dropdown className="cust-drop">
                              <Dropdown.Toggle
                                variant="link"
                                id="dropdown-basic"
                              >
                                <FaEllipsisV className="text-A8B0B9 fs-12" />
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  onClick={() => handleEditClick(user)}
                                  href="#"
                                  className="fs-12 pt-2 pb-2"
                                >
                                  <GrEdit className="align-middle fs-12 me-1" />{" "}
                                  Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleDeleteClick(user)}
                                  className="fs-12 pt-2 pb-2"
                                  href="#"
                                >
                                  <RiDeleteBin5Line className="align-middle fs-14 me-1" />{" "}
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-center mt-4">
              <nav>
                <ul className="pagination">
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    >
                      &laquo;
                    </button>
                  </li>
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      &lt;
                    </button>
                  </li>
                  {pageNumbers.map((number) => (
                    <li
                      key={number}
                      className={`page-item ${
                        currentPage === number ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(number)}
                      >
                        {number}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      &gt;
                    </button>
                  </li>
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      &raquo;
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </Card.Body>
        </Card>
      )}

      <Modal
        show={showAddUserModal}
        onHide={() => {
          setShowAddUserModal(false);
          setNewUser({
            username: "",
            password: "",
            email: "",
            contactNo: "",
            accountId: "",
            roles: ["USER"],
          });
        }}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Add New User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleAddUser} noValidate>
            <Row>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                    Username <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    placeholder="Enter username"
                    value={newUser.username}
                    onChange={handleInputChange}
                    className={`br-radius-8 ${
                      validationErrors.username ? "is-invalid" : ""
                    }`}
                    required
                  />
                  {validationErrors.username && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.username}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                    Password <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type={showNewUserPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter password"
                      value={newUser.password}
                      onChange={handleInputChange}
                      className={`br-radius-8 ${
                        validationErrors.password ? "is-invalid" : ""
                      }`}
                      style={{
                        paddingRight: validationErrors.password
                          ? "60px"
                          : "40px",
                      }}
                      required
                    />
                    <span
                      className="position-absolute top-50 translate-middle-y fs-12 text-success"
                      style={{
                        right: validationErrors.password ? "30px" : "10px",
                        cursor: "pointer",
                        zIndex: 10,
                      }}
                      onClick={() => setShowNewUserPassword((prev) => !prev)}
                      aria-label={
                        showNewUserPassword ? "Hide password" : "Show password"
                      }
                    >
                      <FontAwesomeIcon
                        icon={showNewUserPassword ? faEyeSlash : faEye}
                      />
                    </span>
                  </div>
                  {validationErrors.password && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.password}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter email"
                    value={newUser.email}
                    onChange={handleInputChange}
                    className={`br-radius-8 ${
                      validationErrors.email ? "is-invalid" : ""
                    }`}
                    required
                  />
                  {validationErrors.email && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.email}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                    Contact Number <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="contactNo"
                    placeholder="Enter contact number"
                    value={newUser.contactNo}
                    onChange={handleInputChange}
                    className={`br-radius-8 ${
                      validationErrors.contactNo ? "is-invalid" : ""
                    }`}
                    required
                  />
                  {validationErrors.contactNo && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.contactNo}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                    Account <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="accountId"
                    className={`br-radius-8 ${
                      validationErrors.accountId ? "is-invalid" : ""
                    }`}
                    value={newUser.accountId}
                    onChange={handleInputChange}
                    required
                    disabled={!isSuperAdmin}
                  >
                    <option value="">Select the Company</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.accountName}
                      </option>
                    ))}
                  </Form.Select>
                  {validationErrors.accountId && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.accountId}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                    Role <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="roles"
                    className={`br-radius-8 ${
                      validationErrors.roles ? "is-invalid" : ""
                    }`}
                    value={newUser.roles[0]}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Role</option>
                    {availableRoles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </Form.Select>
                  {validationErrors.roles && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.roles}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-top p-3">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="light bg-white"
              className="flex-grow-1 br-radius-8"
              onClick={() => setShowAddUserModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1 br-radius-8"
              onClick={handleAddUser}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Adding...
                </>
              ) : (
                "Add User"
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        size="md"
      >
        <Modal.Body className="text-center p-4">
          <h5 className="text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Confirm Delete
          </h5>
          <p className="fw-400 fs-12 mb-1">
            Are you sure you want to delete this user?
          </p>
          {userToDelete && (
            <p className="fw-600 mt-2 mb-0 fs-14">{userToDelete.username}</p>
          )}
        </Modal.Body>
        <Modal.Footer
          className="border-none  pt-0 p-3 border-0"
          style={{ justifyContent: "center !important" }}
        >
          <div className="d-flex gap-3 w-75">
            <Button
              variant="light br-radius-8p"
              className="flex-grow-1"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-grow-1"
              onClick={confirmDelete}
            >
              Yes, Delete
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Edit User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {userToEdit && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Contact Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="contactNo"
                  placeholder="Enter contact number"
                  className={`br-radius-8 ${
                    editValidationErrors.contactNo ? "is-invalid" : ""
                  }`}
                  value={editFormData.contactNo}
                  onChange={handleEditFormChange}
                />
                {editValidationErrors.contactNo && (
                  <div className="invalid-feedback d-block">
                    {editValidationErrors.contactNo}
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="Enter email"
                  className={`br-radius-8 ${
                    editValidationErrors.email ? "is-invalid" : ""
                  }`}
                  value={editFormData.email}
                  onChange={handleEditFormChange}
                />
                {editValidationErrors.email && (
                  <div className="invalid-feedback d-block">
                    {editValidationErrors.email}
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Role <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="role"
                  className={`br-radius-8 ${
                    editValidationErrors.role ? "is-invalid" : ""
                  }`}
                  value={editFormData.role}
                  onChange={handleEditFormChange}
                >
                  <option value="">Select role</option>
                  {availableRoles.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </Form.Select>
                {editValidationErrors.role && (
                  <div className="invalid-feedback d-block">
                    {editValidationErrors.role}
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Status <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="status"
                  className={`br-radius-8 ${
                    editValidationErrors.status ? "is-invalid" : ""
                  }`}
                  value={editFormData.status}
                  onChange={handleEditFormChange}
                >
                  <option value="">Select status</option>
                  {availableStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
                {editValidationErrors.status && (
                  <div className="invalid-feedback d-block">
                    {editValidationErrors.status}
                  </div>
                )}
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top p-3">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="light"
              className="flex-grow-1 br-radius-8"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1 br-radius-8"
              onClick={saveEditChanges}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Container>
  );
};

export default UserManagement;
